import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Briefcase,
  GraduationCap,
  IndianRupee,
  CalendarDays,
  Users,
  ArrowRight,
  Map,
  X,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { http } from "../axios/axios";
import { Helmet } from "react-helmet-async";

const InputField = ({
  label,
  name,
  type = "text",
  placeholder,
  register,
  accept,
  errors = {},
  validation = {},
  as = "input",
  rows = 4,
  options = [],
}) => {
  const inputClass =
    "w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm md:px-4 md:py-3 md:text-base outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

  return (
    <div>
      <label
        htmlFor={name}
        className="mb-1.5 block text-sm font-semibold text-slate-700 md:mb-2 md:text-base"
      >
        {label}
        {validation?.required && <span className="text-red-500"> *</span>}
      </label>

      {as === "textarea" ? (
        <textarea
          id={name}
          rows={rows}
          placeholder={placeholder}
          {...register(name, validation)}
          className={`${inputClass} resize-none`}
        />
      ) : as === "select" ? (
        <select
          id={name}
          {...register(name, validation)}
          className={inputClass}
        >
          <option value="">{placeholder}</option>

          {options.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={name}
          type={type}
          accept={accept}
          placeholder={placeholder}
          {...register(name, validation)}
          className={inputClass}
        />
      )}

      {errors[name] && (
        <p className="mt-1 text-xs text-red-500 md:text-sm">
          {errors[name].message}
        </p>
      )}
    </div>
  );
};

const CareerForm = ({ onClose, selectedCareer }) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,

    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();

      Object.keys(data).forEach((key) => {
        if (key !== "profilePicture" && key !== "document") {
          formData.append(key, data[key]);
        }
      });

      if (data.profilePicture?.[0]) {
        formData.append("profilePicture", data.profilePicture[0]);
      }

      if (data.document?.[0]) {
        formData.append("document", data.document[0]);
      }

      formData.append("careerId", selectedCareer?._id);
      formData.append("careerType", selectedCareer?.type);

      // Debug
      for (const [key, value] of formData.entries()) {
        console.log(key, value);
      }

      await http.post("application/apply", formData);

      // console.log(response.data);

      reset();

      if (preview) {
        URL.revokeObjectURL(preview);
      }
      setPreview(null);

      setDocument(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (documentInputRef.current) {
        documentInputRef.current.value = "";
      }

      onClose();

      toast.success("Application submitted successfully!");
    } catch (error) {
      console.error(error);
    }
  };
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);
  const profilePictureRegister = register("profilePicture", {
    onChange: (e) => {
      const file = e.target.files?.[0];

      if (file) {
        setPreview(URL.createObjectURL(file));
      }
    },
  });

  const [document, setDocument] = useState(null);
  const documentInputRef = useRef(null);

  const documentRegister = register("document", {
    onChange: (e) => {
      const file = e.target.files?.[0];
      setDocument(file || null);
    },
  });

  return (
    <motion.div
      className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
        w-[92%] md:max-w-4xl max-h-[80vh] overflow-y-auto rounded-md bg-white shadow-2xl "
      >
        <div className="flex items-center justify-between px-4 py-8 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {selectedCareer?.type === "Job"
                ? "Apply for Job"
                : "Enroll in Course"}
            </h2>

            <p className="text-sm text-gray-600 mt-4">
              {selectedCareer?.type === "Job"
                ? "Complete the form below to apply for this position."
                : "Complete the form below to enroll in this course."}
            </p>
          </div>

          <button
            onClick={onClose}
            className="h-10 w-10 rounded-lg bg-gray-100 hover:bg-red-500 hover:text-white transition flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 border-t border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            Description
          </h3>

          <p className="mt-2 text-sm leading-7 font-medium text-gray-700">
            {selectedCareer?.description}
          </p>
        </div>
        {selectedCareer?.skills?.length > 0 && (
          <div className=" px-6 py-5">
            <p className="mb-2 text-sm font-semibold text-gray-800">
              Required Skills
            </p>

            <div className="flex flex-wrap gap-2">
              {selectedCareer.skills.map((skill, index) => (
                <span
                  key={`${skill}-${index}`}
                  className="rounded-full bg-blue-50 border border-blue-200 px-3 py-1 text-xs font-medium text-blue-700"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="px-6 pt-8 pb-4 border-b border-gray-100">
            <label className="block text-sm font-semibold text-slate-700 mb-4">
              Profile Picture
            </label>

            <div className="flex items-center gap-6">
              <div className="relative h-18 w-18 md:h-25 md:w-25">
                <label htmlFor="profilePicture" className="cursor-pointer">
                  <img
                    src={preview || "https://placehold.co/120x120?text=Photo"}
                    alt="Profile Preview"
                    className="h-18 w-18 md:h-25 md:w-25 rounded-full border-2 border-gray-300 object-cover hover:opacity-80 transition"
                  />
                </label>

                {preview && (
                  <button
                    type="button"
                    onClick={() => {
                      if (preview) {
                        URL.revokeObjectURL(preview);
                      }

                      setPreview(null);
                      setValue("profilePicture", null);

                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                    className="absolute -right-2 -top-2 h-7 w-7 rounded-full bg-red-500 text-white flex items-center justify-center shadow"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div>
                <p className="font-medium text-gray-700">
                  Click the image to upload
                </p>
                <p className="text-sm text-gray-500">
                  JPG, PNG or WEBP (Max 2MB)
                </p>
              </div>
            </div>

            <input
              id="profilePicture"
              type="file"
              accept="image/*"
              className="hidden"
              {...profilePictureRegister}
              required
              ref={(e) => {
                profilePictureRegister.ref(e);
                fileInputRef.current = e;
              }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-6 py-8">
            {/* Common Fields */}
            <InputField
              label="Full Name"
              name="name"
              placeholder="Enter your full name"
              register={register}
              errors={errors}
              validation={{
                required: "Full name is required",
              }}
            />

            <InputField
              label="Email"
              name="email"
              placeholder="Enter your email"
              register={register}
            />

            <InputField
              label="Phone Number"
              name="phoneNumber"
              placeholder="Enter your Phone Number"
              register={register}
              validation={{
                required: "Phone number is required",
              }}
            />

            <InputField
              label="Address"
              name="address"
              placeholder="Enter your Address"
              register={register}
              validation={{
                required: "Address is required",
              }}
            />

            <InputField
              label="Gender"
              name="gender"
              register={register}
              as="select"
              options={[
                { value: "male", label: "Male" },
                { value: "female", label: "Female" },
                { value: "others", label: "Others" },
              ]}
            />

            <InputField
              label="Highest Qualification"
              name="qualification"
              placeholder="Enter your highest qualification"
              register={register}
              validation={{
                required: "Qualification is required",
              }}
            />

            {/* Job Fields */}
            {selectedCareer?.type === "Job" && (
              <>
                <InputField
                  label="Years of Experience"
                  name="experience"
                  placeholder="Enter total experience"
                  register={register}
                  validation={{
                    required: "Experience is required",
                  }}
                />

                <InputField
                  label="Current Company"
                  name="currentCompany"
                  placeholder="Enter your current company"
                  register={register}
                  validation={{
                    required: "Current Company is required",
                  }}
                />

                <InputField
                  label="Current CTC"
                  name="currentCtc"
                  placeholder="Enter your current CTC"
                  register={register}
                  validation={{
                    required: "Current CTC is required",
                  }}
                />

                <InputField
                  label="Expected CTC"
                  name="expectedCtc"
                  placeholder="Enter your expected CTC"
                  register={register}
                  validation={{
                    required: "Expected CTC is required",
                  }}
                />

                <InputField
                  label="Notice Period"
                  name="noticePeriod"
                  placeholder="Immediate / 30 Days / 60 Days"
                  register={register}
                />
              </>
            )}

            {/* Course Fields */}
            {selectedCareer?.type === "Course" && (
              <>
                <InputField
                  label="Current Occupation"
                  name="currentOccupation"
                  placeholder="Student / Working Professional"
                  register={register}
                  validation={{
                    required: "Current Occupation is required",
                  }}
                />

                <InputField
                  label="Reason for Joining"
                  name="reason"
                  rows={1}
                  as="textarea"
                  placeholder="Why do you want to join this course?"
                  register={register}
                  validation={{
                    required: "Reason is required",
                  }}
                />
              </>
            )}
          </div>
          {selectedCareer?.type === "Job" && (
            <div className="px-6 pt-8 pb-4 border-b border-gray-100">
              <label className="block text-sm font-semibold text-slate-700 mb-4">
                Supporting Document (Resume / CV)
              </label>
              <label
                htmlFor="document"
                className="flex cursor-pointer items-center justify-between rounded-lg border-2 border-dashed border-gray-300 p-4 transition hover:border-blue-500 hover:bg-blue-50"
              >
                <div>
                  <p className="font-medium text-gray-700">
                    {document ? document.name : "Click to upload Resume / CV"}
                  </p>

                  <p className="text-sm text-gray-500">
                    PDF, DOC, DOCX (Max 5 MB)
                  </p>
                </div>

                <span className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white">
                  Browse
                </span>
              </label>
              <input
                id="document"
                type="file"
                accept=".pdf,.doc,.docx"
                className="hidden "
                required
                {...documentRegister}
                ref={(e) => {
                  documentRegister.ref(e);
                  documentInputRef.current = e;
                }}
              />
              {document && (
                <div className="mt-3 flex items-center justify-between rounded-lg bg-gray-100 px-4 py-2">
                  <span className="truncate text-sm text-gray-700">
                    📄 {document.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setDocument(null);
                      setValue("document", null);
                      if (documentInputRef.current) {
                        documentInputRef.current.value = "";
                      }
                    }}
                    className="rounded-full bg-red-500 px-2 py-1 text-xs text-white hover:bg-red-600"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="px-6 pb-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-lg border border-gray-300"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Submit Application
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

const Careers = () => {
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("Job");
  const [selectedCareer, setSelectedCareer] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: 30,
  });
  const [openModal, setOpenModal] = useState(false);

  const onClose = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    const getCareers = async () => {
      try {
        setLoading(true);

        const { page, limit } = pagination;

        const res = await http.get("/career/filter", {
          params: {
            type: activeFilter,
            page,
            limit,
          },
        });

        setCareers(res.data.data || []);

        const p = res.data?.pagination;

        setPagination({
          page: p.page,
          totalPages: p.totalPage,
          total: p.total,
          limit,
        });
      } catch (err) {
        console.error("Career fetch failed", err);
      } finally {
        setLoading(false);
      }
    };

    getCareers();
  }, [activeFilter, pagination?.page]);

  const filters = [
    { label: "Jobs", value: "Job" },
    { label: "Courses", value: "Course" },
  ];

  const [seo, setSeo] = useState(null);

  useEffect(() => {
    fetchSeo();
  }, []);

  const fetchSeo = async () => {
    try {
      const { data } = await http.get("/seo/Careers");
      setSeo(data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const seoData = seo || {
    metaTitle: "",
    metaDescription: "",
    metaTags: [],
    schemaScript: null,
  };

  const schemaScript = seoData?.schemaScript
    ? typeof seoData.schemaScript === "string"
      ? seoData.schemaScript
      : JSON.stringify(seoData.schemaScript)
    : null;
  return (
    <>
      <Helmet>
        <title>{seoData?.metaTitle}</title>
        <meta name="description" content={seoData?.metaDescription} />
        <meta
          name="keywords"
          content={
            Array.isArray(seoData?.metaTags)
              ? seoData?.metaTags.join(", ")
              : seoData?.metaTags
          }
        />
        {schemaScript && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: schemaScript }}
          />
        )}
      </Helmet>

      <div className="min-h-screen bg-white pb-20 font-sans">
        <header className="bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 py-20 px-6 text-center relative overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight font-serif">
              Build Your <span className="text-blue-400">Career</span>
            </h1>
            <p className=" hidden md:block mt-6 text-lg text-gray-300 leading-relaxed ">
              Explore exciting career opportunities with us and become part of a
              team that's shaping the future of electronic security solutions.
              Whether you're an experienced professional or just starting your
              journey, we offer roles in CCTV surveillance, access control,
              biometric systems, networking, technical support, project
              execution, and sales. Join us to grow your skills, work on
              innovative security projects, and build a rewarding career.
            </p>
          </div>
        </header>

        <nav
          className="sticky top-20 z-40 border-b border-gray-200 bg-white/95 px-4 py-4 backdrop-blur-md sm:px-6"
          aria-label="Career filters"
        >
          <div className="mx-auto max-w-7xl">
            <div className="flex overflow-x-auto scrollbar-hide">
              <div className="mx-auto flex min-w-max flex-nowrap gap-3 lg:min-w-0 lg:flex-wrap lg:justify-center">
                {filters.map(({ label, value }) => {
                  const isActive = activeFilter === value;

                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setActiveFilter(value);
                        setPagination((prev) => ({
                          ...prev,
                          page: 1,
                        }));
                      }}
                      aria-pressed={isActive}
                      aria-label={`View ${label.toLowerCase()} opportunities`}
                      className={`whitespace-nowrap rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wide transition-all duration-300 sm:px-6 sm:py-2.5 sm:text-sm ${
                        isActive
                          ? "border-blue-600 bg-blue-600 text-white shadow-md"
                          : "border-gray-300 bg-white text-gray-700 hover:border-blue-600 hover:text-blue-600"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </nav>

        <section className="px-4 mt-6 md:px-6 md:mt-16">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {careers.map((career) => (
              <article
                key={career?._id}
                className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      career.type === "Job"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-purple-100 text-purple-700"
                    }`}
                  >
                    {career?.type}
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      career.status === "Active"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-600"
                    }`}
                  >
                    {career?.status}
                  </span>
                </div>
                <h3 className="mt-5 text-xl font-bold text-gray-900 line-clamp-2">
                  {career?.title}
                </h3>
                <div className="mt-3 flex items-center gap-2 text-gray-500">
                  <MapPin size={16} />
                  {career?.location}
                </div>
                <p className="mt-4 text-sm leading-6 text-gray-600 line-clamp-2">
                  {career.description}
                </p>

                {career?.type === "Job" && (
                  <div className="mt-6 space-y-2 text-sm text-gray-700">
                    <p>
                      <strong>Department:</strong> {career.department}
                    </p>

                    <p>
                      <strong>Employment:</strong> {career.employmentType}
                    </p>

                    <p>
                      <strong>Experience:</strong> {career.experience} Years
                    </p>

                    <p>
                      <strong>Salary:</strong> {career.salary}
                    </p>

                    <p>
                      <strong>Vacancies:</strong> {career.vacancies}
                    </p>
                  </div>
                )}

                {career?.type === "Course" && (
                  <div className="mt-6 space-y-2 text-sm text-gray-700">
                    <p>
                      <strong>Duration:</strong> {career.courseDuration}
                    </p>

                    <p>
                      <strong>Course Fee:</strong> ₹
                      {career.courseFee.toLocaleString()}
                    </p>
                  </div>
                )}

                <div className="mt-6 flex items-center justify-between border-t pt-5">
                  <div>
                    <p className="text-xs text-gray-500">Last Date</p>
                    <p className="">
                      {new Date(career?.lastDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div
                    onClick={() => {
                      setSelectedCareer(career);
                      setOpenModal(true);
                    }}
                    className="flex cursor-pointer items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                  >
                    View Details
                    <ArrowRight size={16} />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <AnimatePresence>
          {openModal && (
            <CareerForm onClose={onClose} selectedCareer={selectedCareer} />
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default Careers;
